/**
 * ModelPicker — chat-style multi-LLM selector.
 *
 * Sits inline above any chat composer (teacher or student) and lets the user
 * pick the model that will answer the next message:
 *
 *   • Auto    — backend routes by query type + subject (default)
 *   • Claude Sonnet 4.5 — STEM, math, chemistry, equations
 *   • GPT-5.2 — code, structured outputs
 *   • Gemini 3 Pro — diagrams, visuals, life-sciences
 *
 * The component is intentionally tiny: a current-value pill that opens a
 * dropdown card with each option, its tagline, and a short description.
 * The pill colour mirrors the picked model's accent so the user always sees,
 * at a glance, who is going to answer.
 *
 * Props
 * ─────
 *   value            string  ('auto' | 'claude' | 'gpt' | 'gemini')
 *   onChange         (key) => void
 *   models           array (optional) — pre-fetched catalog from /api/ai/models
 *   size             'sm' | 'md'  (defaults to 'sm')
 *   align            'left' | 'right'  (defaults to 'left')
 *   maxWidth         number (px) — caps the dropdown panel width so it never
 *                    spills past a narrow host container (e.g. a resizable
 *                    chat side panel with `overflow: hidden`, where the panel
 *                    would otherwise get its left edge clipped off)
 *   className        extra wrapper classes
 *   testIdPrefix     base for data-testid attrs (defaults to 'model-picker')
 *
 * Identical UX is used in:
 *   - Teacher Editor → chat dock (TeacherDashboard.js)
 *   - Student Tutor  → LearnNowPage chat composer
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Sparkles, Check } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hard-coded fallback so the picker still renders without a network call.
const DEFAULT_CATALOG = {
  auto:   { key: 'auto',   label: 'Auto',              tagline: 'Pick the best model',     accent: '#0F172A', description: 'Routes math/STEM to Claude, code to GPT-5.2, visuals to Gemini.' },
  claude: { key: 'claude', label: 'Claude Sonnet 4.5', tagline: 'STEM & equations',         accent: '#D97757', description: 'Best for math, physics, chemistry, and step-by-step reasoning.' },
  gpt:    { key: 'gpt',    label: 'GPT-5.2',            tagline: 'Code & structure',         accent: '#10A37F', description: 'Best for code, structured outputs, and formal explanations.' },
  gemini: { key: 'gemini', label: 'Gemini 3 Pro',       tagline: 'Visual & life-sciences',   accent: '#4285F4', description: 'Best for diagrams, visual explanations, and life-sciences.' },
};

let _catalogCache = null;

async function fetchCatalog() {
  if (_catalogCache) return _catalogCache;
  try {
    const token = localStorage.getItem('desky_token') || localStorage.getItem('student_token');
    const { data } = await axios.get(`${API}/ai/models`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      timeout: 4000,
    });
    const map = { auto: data.auto };
    for (const m of (data.models || [])) map[m.key] = m;
    _catalogCache = map;
    return map;
  } catch {
    return DEFAULT_CATALOG;
  }
}

export default function ModelPicker({
  value = 'auto',
  onChange,
  models = null,
  size = 'sm',
  align = 'left',
  anchor = 'bottom', // 'bottom' opens the panel downward; 'top' opens it upward (for composers at the bottom of the viewport)
  maxWidth = null,
  className = '',
  testIdPrefix = 'model-picker',
}) {
  const [catalog, setCatalog] = useState(models || DEFAULT_CATALOG);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (models) { setCatalog(models); return; }
    let cancelled = false;
    fetchCatalog().then(c => { if (!cancelled) setCatalog(c); });
    return () => { cancelled = true; };
  }, [models]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const active = catalog[value] || catalog.auto || DEFAULT_CATALOG.auto;
  const ordered = useMemo(() => {
    const keys = ['auto', 'claude', 'gpt', 'gemini'];
    return keys.map(k => catalog[k]).filter(Boolean);
  }, [catalog]);

  const pillH = size === 'md' ? 'h-9' : 'h-7';
  const pillText = size === 'md' ? 'text-xs' : 'text-xs';

  return (
    <div ref={ref} className={`relative inline-flex ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`group ${pillH} flex items-center gap-1.5 rounded-full border bg-white px-2.5 ${pillText} font-semibold text-slate-700 shadow-sm hover:shadow-md active:scale-[0.97] transition`}
        style={{ borderColor: `${active.accent}40`, boxShadow: `0 1px 2px ${active.accent}15, inset 0 0 0 1px ${active.accent}08` }}
        data-testid={`${testIdPrefix}-trigger`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="relative inline-flex items-center justify-center w-4 h-4 rounded-full" style={{ background: `${active.accent}1a` }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: active.accent }} />
          {value === 'auto' && (
            <Sparkles className="absolute -top-0.5 -right-0.5 w-2 h-2" style={{ color: active.accent }} />
          )}
        </span>
        <span className="leading-none">{active.label}</span>
        <ChevronDown className="w-3 h-3 text-slate-400 group-hover:text-slate-600 transition-colors" />
      </button>

      {open && (
        <div
          className={`absolute ${anchor === 'top' ? 'bottom-[calc(100%+6px)]' : 'top-[calc(100%+6px)]'} z-50 rounded-2xl bg-white border border-slate-200 p-1.5 max-w-[92vw] ${align === 'right' ? 'right-0' : 'left-0'}`}
          style={{
            width: maxWidth ? Math.max(220, Math.min(300, maxWidth)) : 300,
            boxShadow: '0 14px 40px -10px rgba(15,23,42,0.18), 0 4px 12px -4px rgba(15,23,42,0.08)',
          }}
          role="listbox"
          data-testid={`${testIdPrefix}-panel`}
        >
          <div className="px-3 pt-2 pb-1.5 flex items-center justify-between">
            <p className="text-xs font-semibold text-slate-400">AI model</p>
            <p className="text-xs text-slate-400">Switches the next reply</p>
          </div>
          <div className="space-y-1">
            {ordered.map(m => {
              const selected = m.key === value;
              return (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => { onChange?.(m.key); setOpen(false); }}
                  className={`w-full text-left rounded-xl px-3 py-2.5 flex items-start gap-2.5 transition ${selected ? 'bg-slate-50 ring-1 ring-slate-200' : 'hover:bg-slate-50/70'}`}
                  data-testid={`${testIdPrefix}-option-${m.key}`}
                  role="option"
                  aria-selected={selected}
                >
                  <span className="mt-0.5 relative shrink-0 w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${m.accent}15`, border: `1px solid ${m.accent}30` }}>
                    {m.key === 'auto'
                      ? <Sparkles className="w-3.5 h-3.5" style={{ color: m.accent }} />
                      : <span className="w-2 h-2 rounded-full" style={{ background: m.accent }} />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-xs font-semibold text-slate-800 truncate">{m.label}</p>
                      <span className="text-xs font-semibold px-1.5 py-0.5 rounded" style={{ background: `${m.accent}12`, color: m.accent }}>{m.tagline}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 leading-snug">{m.description}</p>
                  </div>
                  {selected && <Check className="w-3.5 h-3.5 text-slate-500 shrink-0 mt-1" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * <ModelBadge /> — used in chat bubbles to show which model wrote the reply.
 * key: 'auto' | 'claude' | 'gpt' | 'gemini'
 */
export function ModelBadge({ modelKey, reason, className = '', testId = 'model-badge' }) {
  const m = DEFAULT_CATALOG[modelKey] || DEFAULT_CATALOG.auto;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs font-semibold ${className}`}
      style={{ background: `${m.accent}10`, color: m.accent, border: `1px solid ${m.accent}25` }}
      title={reason ? `${m.label} — ${reason}` : m.label}
      data-testid={testId}
    >
      <span className="w-1 h-1 rounded-full" style={{ background: m.accent }} />
      {m.label}
    </span>
  );
}

export { DEFAULT_CATALOG as MODEL_CATALOG };
